import React, { useState } from 'react';
import { OpportunityItem, TechnicalStatus, OpportunityStatus } from '../models/opportunity';
import { OpportunityDetailModal } from './OpportunityDetailModal';

const isSlaCritical = (d?: string) => !!d && (new Date(d).getTime() - Date.now()) / 3600000 <= 48;

const statusColor = (s: TechnicalStatus): { bg: string; text: string } => {
  if (s === 'Parecer Técnico Concluído' || s === 'Concluído com Sucesso')
    return { bg: 'rgba(34,197,94,.15)', text: '#4ADE80' };
  if (s === 'Em Avaliação Técnica')
    return { bg: 'rgba(245,158,11,.15)', text: '#FBBF24' };
  if (s === 'Aguardando Info Comercial/Cliente')
    return { bg: 'rgba(239,68,68,.15)', text: '#F87171' };
  if (s === 'Em Implementação')
    return { bg: 'rgba(37,99,235,.15)', text: '#60A5FA' };
  return { bg: 'rgba(136,146,170,.12)', text: '#8892AA' };
};

const oppStatusColor: Record<string, string> = {
  '5. Ganho / Aceite':       '#22C55E',
  '6. Perdido / Cancelado':  '#EF4444',
};

interface CardProps {
  item: OpportunityItem;
  currentUserEmail: string;
  onUpdate: (id: string, status: TechnicalStatus, parecer: string, historicoActual: string) => Promise<void>;
  onAdvanceStatus: (id: string, newStatus: OpportunityStatus) => Promise<void>;
}

export const OpportunityCard: React.FC<CardProps> = ({ item, currentUserEmail, onUpdate, onAdvanceStatus }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [novoStatus, setNovoStatus] = useState<TechnicalStatus>(item.statusTecnico);
  const [parecerTexto, setParecerTexto] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const urgent = isSlaCritical(item.dataLimiteParecer);
  const sc = statusColor(item.statusTecnico);
  const isGanho = item.statusOportunidade === '5. Ganho / Aceite';
  const isPerdido = item.statusOportunidade === '6. Perdido / Cancelado';

  const borderColor = isGanho ? '#22C55E'
    : isPerdido ? '#EF4444'
    : urgent ? '#EF4444'
    : item.statusTecnico === 'Parecer Técnico Concluído' ? '#22C55E'
    : '#F59E0B';

  const slaPercent = (() => {
    if (!item.dataLimiteParecer) return 0;
    const now = Date.now();
    const limit = new Date(item.dataLimiteParecer).getTime();
    const total = 7 * 24 * 3600000;
    return Math.min(100, Math.max(0, ((total - (limit - now)) / total) * 100));
  })();

  const historico = (item.historicoParecer || '').split('\n\n---\n\n').filter(e => e.trim());

  const handleSubmit = async () => {
    if (!parecerTexto.trim()) return;
    setSubmitting(true);
    try {
      await onUpdate(item.id, novoStatus, parecerTexto, item.historicoParecer || '');
      setShowModal(false);
      setParecerTexto('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ── CARD ── */}
      <div style={{
        background: 'var(--chk-card)', border: '1px solid var(--chk-border)',
        borderLeft: `3px solid ${borderColor}`, borderRadius: 14, overflow: 'hidden',
        transition: 'transform .15s, box-shadow .15s',
        opacity: isPerdido ? 0.7 : 1,
      }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 32px rgba(0,0,0,.4)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = ''; (e.currentTarget as HTMLDivElement).style.boxShadow = ''; }}
      >
        {/* SLA bar */}
        <div style={{ height: 3, background: 'var(--chk-border)' }}>
          <div style={{ height: '100%', width: `${slaPercent}%`, background: borderColor, borderRadius: 2, transition: 'width .3s' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '13px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--chk-border)' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--chk-white)' }}>{item.codigoOportunidade}</div>
            <div style={{ fontSize: 12, color: 'var(--chk-muted)', marginTop: 3 }}>🏢 {item.cliente}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            {(isGanho || isPerdido) ? (
              <div style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${oppStatusColor[item.statusOportunidade]}22`, color: oppStatusColor[item.statusOportunidade] }}>
                {isGanho ? '🏆 Ganho' : '❌ Perdido'}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.text }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: sc.text }} />
                {item.statusTecnico}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '12px 16px' }}>
          {[
            ['Comercial',    item.comercialNome],
            ['Lead Técnico', item.responsavelTecnicoNome || '—'],
            ['Data Limite',  item.dataLimiteParecer || 'Sem prazo'],
          ].map(([lbl, val]) => (
            <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
              <span style={{ fontSize: 11, color: 'var(--chk-muted)' }}>{lbl}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: urgent && lbl === 'Data Limite' ? '#EF4444' : 'var(--chk-text)' }}>
                {urgent && lbl === 'Data Limite' ? `⚠️ ${val}` : val}
              </span>
            </div>
          ))}

          {/* Âmbito */}
          <div style={{ margin: '10px 0', background: 'rgba(255,255,255,.03)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '9px 12px' }}>
            <div style={{ fontSize: 10, color: 'var(--chk-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>Âmbito</div>
            <div style={{ fontSize: 12, color: 'var(--chk-text)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {item.ambitoNecessidades || '—'}
            </div>
          </div>

          {/* Histórico toggle */}
          {historico.length > 0 ? (
            <div>
              <div onClick={() => setShowHistory(!showHistory)} style={{ fontSize: 12, color: '#60A5FA', cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                📋 Histórico de Pareceres ({historico.length}) {showHistory ? '▲' : '▼'}
              </div>
              {showHistory && (
                <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
                  {historico.map((entry, i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,.03)', borderLeft: '3px solid #1755B8', padding: '8px 10px', borderRadius: 6, marginBottom: 6, fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--chk-text)' }}>{entry}</div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--chk-muted)', padding: '4px 0' }}>📄 Sem histórico de pareceres</div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '11px 16px', display: 'flex', gap: 8, borderTop: '1px solid var(--chk-border)', background: 'rgba(0,0,0,.15)' }}>
          {!isGanho && !isPerdido && (
            <button onClick={() => setShowModal(true)} style={{ flex: 1, background: 'linear-gradient(135deg,#CC2936,#9B1D24)', color: 'white', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              ✏️ Atualizar Parecer
            </button>
          )}
          <button onClick={() => setShowDetail(true)} style={{ background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', color: 'var(--chk-text)', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', flex: isGanho || isPerdido ? 1 : 0 }}>
            👁 Ver
          </button>
        </div>
      </div>

      {/* Modal Parecer */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background: 'var(--chk-card)', border: '1px solid var(--chk-border)', borderRadius: 16, width: '100%', maxWidth: 520, overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--chk-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--chk-navy)' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--chk-white)' }}>Parecer Técnico</div>
                <div style={{ fontSize: 12, color: 'var(--chk-muted)', marginTop: 2 }}>{item.codigoOportunidade} — {item.cliente}</div>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: 'var(--chk-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {item.parecerTecnico && (
                <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 10, color: 'var(--chk-muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Último Parecer</div>
                  <div style={{ fontSize: 12, color: 'var(--chk-text)' }}>{item.parecerTecnico}</div>
                </div>
              )}
              <div>
                <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 6 }}>NOVO STATUS TÉCNICO</label>
                <select value={novoStatus} onChange={e => setNovoStatus(e.target.value as TechnicalStatus)}
                  style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '9px 12px', color: 'var(--chk-text)', fontSize: 13 }}>
                  <option value="Em Avaliação Técnica">Em Avaliação Técnica</option>
                  <option value="Aguardando Info Comercial/Cliente">Aguardando Info Comercial/Cliente</option>
                  <option value="Parecer Técnico Concluído">Parecer Técnico Concluído</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--chk-muted)', display: 'block', marginBottom: 6 }}>NOVO PARECER (será adicionado ao histórico)</label>
                <textarea value={parecerTexto} onChange={e => setParecerTexto(e.target.value)} rows={5}
                  placeholder="Escreve o teu parecer técnico..."
                  style={{ width: '100%', background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '10px 12px', color: 'var(--chk-text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              {historico.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--chk-muted)', marginBottom: 6 }}>HISTÓRICO ANTERIOR</div>
                  <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                    {historico.slice(0, 2).map((e, i) => (
                      <div key={i} style={{ background: 'rgba(255,255,255,.03)', borderLeft: '3px solid #1755B8', padding: '7px 10px', borderRadius: 6, marginBottom: 4, fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--chk-muted)' }}>{e}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--chk-border)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'rgba(0,0,0,.2)' }}>
              <button onClick={() => setShowModal(false)} style={{ background: 'var(--chk-navy)', border: '1px solid var(--chk-border)', color: 'var(--chk-muted)', borderRadius: 8, padding: '9px 18px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleSubmit} disabled={submitting || !parecerTexto.trim()}
                style={{ background: submitting || !parecerTexto.trim() ? '#555' : 'linear-gradient(135deg,#CC2936,#9B1D24)', color: 'white', border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: submitting || !parecerTexto.trim() ? 'not-allowed' : 'pointer' }}>
                {submitting ? '⏳ A gravar...' : '✅ Submeter Parecer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalhe */}
      {showDetail && (
        <OpportunityDetailModal
          item={item}
          currentUserEmail={currentUserEmail}
          onClose={() => setShowDetail(false)}
          onAdvanceStatus={onAdvanceStatus}
        />
      )}
    </>
  );
};
